import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CollectionsIcon from '@mui/icons-material/Collections'
import DescriptionIcon from '@mui/icons-material/Description'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications'
import {
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography
} from '@mui/material'
import {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { ServiceItem } from '../../hooks/useTransformServiceItems/useTransformServiceItems'
import { EntryWithRelated } from '../../models/inspections'
import { RO_CLOSED } from '../../models/Status'
import { RepairOrderContext } from '../../providers/RepairOrderProvider/RepairOrderProvider'
import { useCreateInspectionEntryServiceAssociationMutation } from '../../redux/api/inspectionApi'
import { InspectionEntryServiceEntry, ServiceEntry } from '../../sdk'
import { getAllRecommendedCannedJobs } from '../Estimates/InspectionDrawer/getAllRecommendedCannedJobs'
import { getAllTechnicianNotes } from '../Estimates/InspectionDrawer/getAllTechnicianNotes'
import { ServiceCardsOfRepairOrder } from '../Estimates/ServiceCardsOfRepairOrder'
import { LookupServiceDialog } from '../Estimates/useLookupServiceDialog/LookupServiceDialog'
import MediaGallery, { MediaEntryType } from '../MediaGallery/MediaGallery'
import { AddressChip } from './AddressChip'
import { CustomerNotesField } from './CustomerNotesField'
import { mapServiceItemAssociations } from './mapServiceAssociations'
import SingleEntryPromptItem from './SingleEntryPromptItem'
import { SingleEntryRecommendedCannedJob } from './SingleEntryRecommendedCannedJob'
import SingleTechnicianNotes from './SingleTechnicianNotes'
interface EditEntryProps {
  services: ServiceItem[] | undefined
  associations: InspectionEntryServiceEntry[] | undefined
  setEditDialog: (id: string) => void
  handleDeferOrRecommendService?: (
    service: ServiceEntry,
    isDeferral: boolean
  ) => Promise<void>
  entry: EntryWithRelated
}

const SingleEntryUI: (props: EditEntryProps) => ReactElement = ({
  entry,
  setEditDialog,
  services,
  associations
}) => {
  const { vehicle, repairOrder } = useContext(RepairOrderContext)
  const isEstimate = !!repairOrder?.entry?.info?.isEstimate
  const readOnly = repairOrder?.status?.id === RO_CLOSED

  const [open, setOpen] = useState(!(entry.status && !entry.status.info!.shop))
  const { accessToken } = useAccessTokenAndShopId()
  const [createAssociation] =
    useCreateInspectionEntryServiceAssociationMutation()
  const filteredAssociations = useMemo(() => {
    return services && mapServiceItemAssociations(entry, services, associations)
  }, [entry, services, associations])

  const filteredServices = useMemo(
    () =>
      (filteredAssociations || [])
        .filter((association) => association.checked)
        .map((association) => association.service),
    [filteredAssociations]
  )

  const cannedJobsInService = useMemo(
    () =>
      new Set(
        (services || [])
          .filter((s) => s.entry!.info!.cannedJob)
          .map((s) => s.entry!.info!.cannedJob) as string[]
      ),
    [services]
  )
  const recommendedJobs = useMemo(
    () => getAllRecommendedCannedJobs(entry),
    [entry]
  )
  const technicianNotes = useMemo(() => getAllTechnicianNotes(entry), [entry])
  const addServiceCallback = useCallback(
    async (serviceIds: string[]) => {
      const promises: Promise<void>[] = []
      serviceIds.forEach((serviceId) => {
        promises.push(
          createAssociation({
            authorization: accessToken,
            inspectionEntryServiceEntry: {
              inspectionEntryId: entry.id!,
              serviceId
            }
          }).unwrap()
        )
      })
      await Promise.all(promises)
    },
    [accessToken, entry.id, createAssociation]
  )
  const [addServiceItemDialogProps, setAddServiceItemDialogProps] =
    useState(false)
  useEffect(() => {
    if (entry.info!.addressed) {
      setOpen(false)
    }
  }, [entry])
  const [customerNotesLoading, setCustomerNotesLoading] = useState(false)
  if (entry.prompts.length === 0) {
    return <></>
  }
  const prompt = entry.prompts[0]
  return (
    <>
      {addServiceItemDialogProps && (
        <LookupServiceDialog
          setCustomProps={setAddServiceItemDialogProps}
          addServiceCallback={addServiceCallback}
        />
      )}
      <Box sx={{ border: '1px solid rgba(0, 0, 0, 0.24)', borderRadius: 2 }}>
        <ListItemButton onClick={() => setOpen(!open)}>
          <ListItemIcon>
            {entry.info!.addressed ||
            (entry.status && !entry.status.info!.shop) ? (
              <CheckIcon color="success" />
            ) : (
              <SettingsApplicationsIcon />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="h5" color="primary.main">
                {entry.info!.title || '(No title)'}
              </Typography>
            }
          />
          <ListItemSecondaryAction>
            <Stack direction="row" spacing={1}>
              {technicianNotes.length > 0 ? (
                <IconButton onClick={() => {}} color={'primary'}>
                  <DescriptionIcon />
                </IconButton>
              ) : (
                <></>
              )}
              {prompt.media.length > 0 ? (
                <IconButton
                  onClick={(event) => {
                    event?.stopPropagation()
                    setOpen(true)
                  }}
                  color={'primary'}>
                  <Badge badgeContent={prompt.media.length} color="secondary">
                    <CollectionsIcon />
                  </Badge>
                </IconButton>
              ) : (
                <></>
              )}
              <AddressChip inspectionEntry={entry} />
              {open ? <ExpandLess /> : <ExpandMore />}
            </Stack>
          </ListItemSecondaryAction>
        </ListItemButton>
        {open && <Divider />}
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Grid container direction="row" spacing={2} p={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6">Questions</Typography>
              <List sx={{ width: '100%' }}>
                <SingleEntryPromptItem prompt={prompt} key={prompt.id} />
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6">Technician Recommended Jobs</Typography>
              <List>
                {recommendedJobs.length > 0 ? (
                  recommendedJobs.map((cannedJob) => (
                    <SingleEntryRecommendedCannedJob
                      key={cannedJob.entry!.id!}
                      {...{
                        cannedJob,
                        cannedJobsInService
                      }}
                      inspectionEntry={entry}
                    />
                  ))
                ) : (
                  <ListItem disablePadding>
                    <ListItemText primary="No recommended jobs" />
                  </ListItem>
                )}
              </List>
              <Stack justifyContent={'flex-end'} spacing={1} direction="row">
                <Button
                  onClick={(event) => {
                    event.stopPropagation()
                    setEditDialog(entry.id!)
                  }}
                  size="small"
                  variant="outlined">
                  Edit
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6">Technician Notes</Typography>
              <Stack sx={{ width: '100%' }}>
                <SingleTechnicianNotes
                  prompt={prompt}
                  entry={entry}
                  setCustomerNotesLoading={setCustomerNotesLoading}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6">Customer Notes</Typography>
              <CustomerNotesField
                {...{ entry, customerNotesLoading, setCustomerNotesLoading }}
              />
            </Grid>
          </Grid>
          <Divider />
          <Stack p={2} spacing={1}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="h6">Services</Typography>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="contained"
                onClick={() => setAddServiceItemDialogProps(true)}>
                Service
              </Button>
            </Stack>
            {services && (
              <ServiceCardsOfRepairOrder
                serviceItems={filteredServices}
                allServiceItems={services}
                {...{ isEstimate, vehicle, readOnly }}
              />
            )}
          </Stack>
          <Divider />
          <Stack p={2}>
            <MediaGallery
              mediaEntryType={MediaEntryType.inspectionPrompt}
              media={prompt.media}
              parentId={prompt.id!}
              isMobileDevice={false}
            />
          </Stack>
        </Collapse>
      </Box>
    </>
  )
}

export default SingleEntryUI
